import {css, StyleSheet} from 'aphrodite';
import Backbone from 'backbone';
import classNames from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';

import * as ReduxDOMRender from 'redux-stores/ReduxDOMRender';
import SectionTabs from 'ui-library/SectionTabs';

export function getTabAndPanelIds(key: string): {key: string; tabId: string; panelId: string} {
  return {
    key,
    tabId: `${key}-tab`,
    panelId: `${key}-panel`,
  };
}

export interface Subpanel {
  component: JSX.Element;
  key: string;
  tabId: string;
  panelId: string;
  title: string;
  hideInPanelTabs?: boolean;
}

interface ReactPanelContainerProps {
  subpanels: Array<Subpanel>;
  activeKey?: string;
  onTabClick: (key: string) => void;
}

function ReactPanelContainer(props: ReactPanelContainerProps): JSX.Element {
  const panelTabs = props.subpanels.filter((subpanel) => !(subpanel.hideInPanelTabs ?? false));
  const isPanelWithNoTabs = panelTabs.length === 0;
  const activeKey = props.activeKey ?? props.subpanels[0].key;
  const activePanel = props.subpanels.find((subpanel) => subpanel.key === activeKey) ?? props.subpanels[0];
  return (
    <div>
      {!isPanelWithNoTabs && (
        <div className="panel-title">
          <SectionTabs
            tabs={panelTabs.map(({key, panelId, tabId, title}) => ({
              handleClick: () => {
                props.onTabClick(key);
              },
              ariaControls: panelId,
              id: tabId,
              isActive: activeKey === key,
              text: title,
            }))}
          />
        </div>
      )}
      <div
        aria-labelledby={activePanel.tabId}
        className="panel-content"
        id={activePanel.panelId}
        role="tabpanel"
      >
        <div
          className={classNames('panel-container', css(isPanelWithNoTabs && styles.panelWithNoTabs))}
          style={{height: '100%'}}
        >
          {activePanel.component}
        </div>
      </div>
    </div>
  );
}

// Meant to accompany a react-panel.jade panel - subclass and define getSubpanels.
abstract class ReactPanelBaseView<
  TPanelState extends Backbone.Model,
  TModel extends Backbone.Model,
> extends Backbone.View<TModel> {
  panelState!: TPanelState;
  model!: TModel;

  initialize(): void {
    this.closePanel = this.closePanel.bind(this);
  }

  abstract getSubpanels(): Array<Subpanel>;

  bindListeners(panelState: TPanelState, model: TModel): void {
    if (this.panelState != null) {
      this.stopListening(this.panelState);
    }
    this.panelState = panelState;
    this.listenTo(panelState, 'change', () => {
      this.render();
    });
    if (this.model != null) {
      this.stopListening(this.model);
    }
    this.model = model;
    this.listenTo(model, 'change', () => {
      this.render();
    });
  }

  update(panelState: TPanelState, model: TModel): void {
    this.bindListeners(panelState, model);
    this.render();
  }

  closePanel(): void {
    this.panelState.trigger('close');
  }

  onTabClick(tab: string): void {
    this.panelState.set({activeSubPanel: tab});
  }

  render(): this {
    const mount = this.$('.react-panel-mount')[0];
    if (this.panelState.get('active')) {
      const component = (
        <ReactPanelContainer
          subpanels={this.getSubpanels()}
          activeKey={this.panelState.get('activeSubPanel')}
          onTabClick={(key) => {
            this.onTabClick(key);
          }}
        />
      );
      ReduxDOMRender.renderToDOM(component, mount);
    } else {
      ReactDOM.unmountComponentAtNode(mount);
    }
    return this;
  }
}

const styles = StyleSheet.create({
  panelWithNoTabs: {
    paddingLeft: '0',
    paddingRight: '0',
  },
});

export default ReactPanelBaseView;
